import { pickBy, identity } from "lodash";
import fp from "lodash/fp";
import moment from "moment";

import {
	RiskExposureCalculatorNameFields,
	ExecutiveReasonReviewNameFields,
	ExecutiveSignerNameFields,
	ExecutiveApprovedParametersNameFields,
	ExecutiveEstimatedExposureNameFields,
	ExecutiveRecommendationNameFields,
	ExecutiveFinancialsFormFields,
	ExecutiveProcessingReviewFields,
	DaysToDeliveryFieldsNames,
} from "globalConstants";
import {
	convertDateToISO,
	getDateWithoutTime,
	getFormattedDate,
	getFullName,
	getOnlyDate,
	safeParseFloat,
	TIME_OFFSET_DAY_END,
} from "helpers";
import { getFeesItems } from "mappers/merchantInfo/merchantInfoMappings";
import { CreditReview, CreditReviewColumns } from "types";
import { float2Normalizer, floatWithComma2Normalizer } from "validators";

const PAGE_SIZE = 50;

export const mapCreditReviewDataFromServer = (data): CreditReview => {
	return {
		id: data?.id,
		merchantName: data?.merchant_name,
		mid: data?.mid,
		priorityType: data?.priority_type,
		salesMonthlyVolume: data?.sales_monthly_volume,
		deliverySameDay: data?.delivery_same_day,
		deliveryFromOneToThreeDays: data?.delivery_from_one_to_three_days,
		deliveryFromFourToSevenDays: data?.delivery_from_four_to_seven_days,
		deliveryFromEightToFourteenDays:
			data?.delivery_from_eight_to_fourteen_days,
		deliveryFromFifteenToThirtyDays:
			data?.delivery_from_fifteen_to_thirty_days,
		deliveryOverThirtyDays: data?.delivery_over_thirty_days,
		creditExposure: data?.credit_exposure,
		uwApprovalDate: data?.uw_approval_date,
		lastReviewDate: data?.last_review_date,
		nextReviewDate: data?.next_review_date,
		assigneeId: data?.assignee_id,
		assignedTo: {
			firstName: data?.assigned_to?.first_name,
			lastName: data?.assigned_to?.last_name,
		},
		merchantId: data?.merchant_id,
	};
};

export const mapFilterCreditReviewDataToServer = ({
	page,
	sortBy,
	filters,
}) => {
	const _filters = fp.cloneDeep(filters);
	return {
		page_index: page,
		page_size: PAGE_SIZE,
		sort_type:
			CreditReviewColumns.getByName(sortBy?.id)?.value?.toString() ||
			CreditReviewColumns.SalesMonthlyVolume.value,
		ascending: sortBy?.id ? sortBy?.desc : false,
		merchant_id: _filters?.mid?.map(item => item?.id),
		sales_monthly_volume_from: safeParseFloat(
			_filters?.monthlyVolumesNumberFrom,
		),
		sales_monthly_volume_to: safeParseFloat(
			_filters?.monthlyVolumesNumberTo,
		),
		credit_exposure_from: safeParseFloat(_filters?.creditNumberFrom),
		credit_exposure_to: safeParseFloat(_filters?.creditNumberTo),
		uw_approval_date_from: convertDateToISO(_filters?.uwApprovalDate?.[0]),
		uw_approval_date_to: convertDateToISO(_filters?.uwApprovalDate?.[1], {
			addTime: TIME_OFFSET_DAY_END,
		}),
		days_to_delivery: _filters?.daysToDelivery?.value,
		assignee_id: _filters?.assignedTo?.id,
	};
};

export const getMappedRiskExposureInfoData = data => {
	return {
		[RiskExposureCalculatorNameFields.assigneeId]: data?.assignee_id,
		[RiskExposureCalculatorNameFields.chargebackRatio]:
			data?.chargeback_ratio,
		[RiskExposureCalculatorNameFields.creditAndCbRiskExposure]:
			data?.credit_and_cb_risk_exposure,
		[RiskExposureCalculatorNameFields.dailyAverageMonthlySalesVolume]:
			data?.daily_average_monthly_sales_volume,
		[RiskExposureCalculatorNameFields.daysBeforeDiscountOrFeesAreCollected]:
			data?.days_before_discount_or_fees_are_collected || 0,
		[DaysToDeliveryFieldsNames.DaysOverThirty.name]:
			data?.[DaysToDeliveryFieldsNames.DaysOverThirty.beName] || 0,
		[RiskExposureCalculatorNameFields.delayedDeliveryRiskExposure]:
			data?.delayed_delivery_risk_exposure,
		[DaysToDeliveryFieldsNames.EightFourteenDays.name]:
			data?.[DaysToDeliveryFieldsNames.EightFourteenDays.beName],
		[DaysToDeliveryFieldsNames.FifteenThirtyDays.name]:
			data?.[DaysToDeliveryFieldsNames.FifteenThirtyDays.beName],
		[DaysToDeliveryFieldsNames.FourSevenDays.name]:
			data?.[DaysToDeliveryFieldsNames.FourSevenDays.beName],
		[DaysToDeliveryFieldsNames.OneThreeDays.name]:
			data?.[DaysToDeliveryFieldsNames.OneThreeDays.beName],
		[DaysToDeliveryFieldsNames.ThirtyPlusDays.name]:
			data?.[DaysToDeliveryFieldsNames.ThirtyPlusDays.beName],
		[DaysToDeliveryFieldsNames.SameDay.name]:
			data?.[DaysToDeliveryFieldsNames.SameDay.beName],
		[RiskExposureCalculatorNameFields.discountAndFeeExposure]:
			data?.discount_and_fee_exposure,
		[RiskExposureCalculatorNameFields.doYouOfferAProductServiceWarrantyOrGuarantee]:
			data?.do_you_offer_a_product_service_warranty_or_guarantee?.toString(),
		[RiskExposureCalculatorNameFields.estimatedPercentFees]:
			data?.estimated_percent_fees,
		[RiskExposureCalculatorNameFields.id]: data?.id,
		[RiskExposureCalculatorNameFields.monthlyVolume]:
			floatWithComma2Normalizer(data?.monthly_volume),
		[RiskExposureCalculatorNameFields.previous120DaysMonthlyVolume]:
			data?.previous_120_days_monthly_volume,
		[RiskExposureCalculatorNameFields.refundRatio]: data?.refund_ratio,
		[RiskExposureCalculatorNameFields.totalRiskExposure]:
			data?.total_risk_exposure,
	};
};

export const mappedUpdateRiskExposureInfoData = formValues => {
	return {
		[RiskExposureCalculatorNameFields.monthlyVolume]:
			formValues?.[RiskExposureCalculatorNameFields.monthlyVolume],
		[DaysToDeliveryFieldsNames.SameDay.beName]:
			Number(formValues?.[DaysToDeliveryFieldsNames.SameDay.name]) || 0,
		[DaysToDeliveryFieldsNames.OneThreeDays.beName]:
			Number(formValues?.[DaysToDeliveryFieldsNames.OneThreeDays.name]) ||
			0,
		[DaysToDeliveryFieldsNames.FourSevenDays.beName]:
			Number(
				formValues?.[DaysToDeliveryFieldsNames.FourSevenDays.name],
			) || 0,
		[DaysToDeliveryFieldsNames.EightFourteenDays.beName]:
			Number(
				formValues?.[DaysToDeliveryFieldsNames.EightFourteenDays.name],
			) || 0,
		[DaysToDeliveryFieldsNames.FifteenThirtyDays.beName]:
			Number(
				formValues?.[DaysToDeliveryFieldsNames.FifteenThirtyDays.name],
			) || 0,
		[DaysToDeliveryFieldsNames.DaysOverThirty.beName]:
			Number(
				formValues?.[DaysToDeliveryFieldsNames.DaysOverThirty.name],
			) || 0,
		[DaysToDeliveryFieldsNames.ThirtyPlusDays.beName]:
			Number(
				formValues?.[DaysToDeliveryFieldsNames.ThirtyPlusDays.name],
			) || 0,
		[RiskExposureCalculatorNameFields.doYouOfferAProductServiceWarrantyOrGuarantee]:
			!fp.isEmpty(
				formValues?.[
					RiskExposureCalculatorNameFields
						.doYouOfferAProductServiceWarrantyOrGuarantee
				],
			) &&
			JSON.parse(
				formValues?.[
					RiskExposureCalculatorNameFields
						.doYouOfferAProductServiceWarrantyOrGuarantee
				],
			),
		[RiskExposureCalculatorNameFields.refundRatio]:
			Number(
				formValues?.[RiskExposureCalculatorNameFields.refundRatio],
			) || 0,
		[RiskExposureCalculatorNameFields.chargebackRatio]:
			Number(
				formValues?.[RiskExposureCalculatorNameFields.chargebackRatio],
			) || 0,
		[RiskExposureCalculatorNameFields.daysBeforeDiscountOrFeesAreCollected]:
			Number(
				formValues?.[
					RiskExposureCalculatorNameFields
						.daysBeforeDiscountOrFeesAreCollected
				],
			) || 0,
		[RiskExposureCalculatorNameFields.estimatedPercentFees]:
			Number(
				formValues?.[
					RiskExposureCalculatorNameFields.estimatedPercentFees
				],
			) || 0,
	};
};

export const getMappedReasonReviewData = data => {
	return {
		[ExecutiveReasonReviewNameFields.reasonForReview]:
			data?.reason_for_review,
		[ExecutiveReasonReviewNameFields.requestingTheFollowing]:
			data?.requesting_the_following,
		[ExecutiveReasonReviewNameFields.nextReviewDate]: getFormattedDate({
			date: data?.next_review_date,
			format: "MM/DD/YYYY",
		}),
	};
};

export const mappedUpdateReasonReviewData = formValues => {
	return {
		[ExecutiveReasonReviewNameFields.reasonForReview]:
			formValues?.[ExecutiveReasonReviewNameFields.reasonForReview],
		[ExecutiveReasonReviewNameFields.requestingTheFollowing]:
			formValues?.[
				ExecutiveReasonReviewNameFields.requestingTheFollowing
			],
		[ExecutiveReasonReviewNameFields.nextReviewDate]: getDateWithoutTime(
			formValues?.[ExecutiveReasonReviewNameFields.nextReviewDate],
		),
	};
};

export const getMappedSignerData = data => {
	const onlySingersWithData = data && pickBy(data, identity);

	const signerOneData = onlySingersWithData?.signer_one && {
		[ExecutiveSignerNameFields.signerOne]: {
			[ExecutiveSignerNameFields.firstName]:
				data?.[ExecutiveSignerNameFields.signerOne]?.[
					ExecutiveSignerNameFields.firstName
				],
			[ExecutiveSignerNameFields.lastName]:
				data?.[ExecutiveSignerNameFields.signerOne]?.[
					ExecutiveSignerNameFields.lastName
				],
			[ExecutiveSignerNameFields.ownership]:
				data?.[ExecutiveSignerNameFields.signerOne]?.[
					ExecutiveSignerNameFields.ownership
				]?.toString(),
			[ExecutiveSignerNameFields.ficoScore]:
				data?.[ExecutiveSignerNameFields.signerOne]?.[
					ExecutiveSignerNameFields.ficoScore
				]?.toString(),
			[ExecutiveSignerNameFields.comment]:
				data?.[ExecutiveSignerNameFields.signerOne]?.[
					ExecutiveSignerNameFields.comment
				],
			[ExecutiveSignerNameFields.signerNumber]:
				ExecutiveSignerNameFields.signerOne,
		},
	};

	const signerTwoData = onlySingersWithData?.signer_two && {
		[ExecutiveSignerNameFields.signerTwo]: {
			[ExecutiveSignerNameFields.firstName]:
				data?.[ExecutiveSignerNameFields.signerTwo]?.[
					ExecutiveSignerNameFields.firstName
				],
			[ExecutiveSignerNameFields.lastName]:
				data?.[ExecutiveSignerNameFields.signerTwo]?.[
					ExecutiveSignerNameFields.lastName
				],
			[ExecutiveSignerNameFields.ownership]:
				data?.[ExecutiveSignerNameFields.signerTwo]?.[
					ExecutiveSignerNameFields.ownership
				]?.toString(),
			[ExecutiveSignerNameFields.ficoScore]:
				data?.[ExecutiveSignerNameFields.signerTwo]?.[
					ExecutiveSignerNameFields.ficoScore
				]?.toString(),
			[ExecutiveSignerNameFields.comment]:
				data?.[ExecutiveSignerNameFields.signerTwo]?.[
					ExecutiveSignerNameFields.comment
				],
			[ExecutiveSignerNameFields.signerNumber]:
				ExecutiveSignerNameFields.signerTwo,
		},
	};

	const signerThreeData = onlySingersWithData?.signer_three && {
		[ExecutiveSignerNameFields.signerThree]: {
			[ExecutiveSignerNameFields.firstName]:
				data?.[ExecutiveSignerNameFields.signerThree]?.[
					ExecutiveSignerNameFields.firstName
				],
			[ExecutiveSignerNameFields.lastName]:
				data?.[ExecutiveSignerNameFields.signerThree]?.[
					ExecutiveSignerNameFields.lastName
				],
			[ExecutiveSignerNameFields.ownership]:
				data?.[ExecutiveSignerNameFields.signerThree]?.[
					ExecutiveSignerNameFields.ownership
				]?.toString(),
			[ExecutiveSignerNameFields.ficoScore]:
				data?.[ExecutiveSignerNameFields.signerThree]?.[
					ExecutiveSignerNameFields.ficoScore
				]?.toString(),
			[ExecutiveSignerNameFields.comment]:
				data?.[ExecutiveSignerNameFields.signerThree]?.[
					ExecutiveSignerNameFields.comment
				],
			[ExecutiveSignerNameFields.signerNumber]:
				ExecutiveSignerNameFields.signerThree,
		},
	};

	const signerFourData = onlySingersWithData?.signer_four && {
		[ExecutiveSignerNameFields.signerFour]: {
			[ExecutiveSignerNameFields.firstName]:
				data?.[ExecutiveSignerNameFields.signerFour]?.[
					ExecutiveSignerNameFields.firstName
				],
			[ExecutiveSignerNameFields.lastName]:
				data?.[ExecutiveSignerNameFields.signerFour]?.[
					ExecutiveSignerNameFields.lastName
				],
			[ExecutiveSignerNameFields.ownership]:
				data?.[ExecutiveSignerNameFields.signerFour]?.[
					ExecutiveSignerNameFields.ownership
				]?.toString(),
			[ExecutiveSignerNameFields.ficoScore]:
				data?.[ExecutiveSignerNameFields.signerFour]?.[
					ExecutiveSignerNameFields.ficoScore
				]?.toString(),
			[ExecutiveSignerNameFields.comment]:
				data?.[ExecutiveSignerNameFields.signerFour]?.[
					ExecutiveSignerNameFields.comment
				],
			[ExecutiveSignerNameFields.signerNumber]:
				ExecutiveSignerNameFields.signerFour,
		},
	};

	return {
		[`${ExecutiveSignerNameFields.signerOne}_${ExecutiveSignerNameFields.comment}`]:
			data?.[ExecutiveSignerNameFields.signerOne]?.[
				ExecutiveSignerNameFields.comment
			],
		[`${ExecutiveSignerNameFields.signerTwo}_${ExecutiveSignerNameFields.comment}`]:
			data?.[ExecutiveSignerNameFields.signerTwo]?.[
				ExecutiveSignerNameFields.comment
			],
		[`${ExecutiveSignerNameFields.signerThree}_${ExecutiveSignerNameFields.comment}`]:
			data?.[ExecutiveSignerNameFields.signerThree]?.[
				ExecutiveSignerNameFields.comment
			],
		[`${ExecutiveSignerNameFields.signerFour}_${ExecutiveSignerNameFields.comment}`]:
			data?.[ExecutiveSignerNameFields.signerFour]?.[
				ExecutiveSignerNameFields.comment
			],
		...signerOneData,
		...signerTwoData,
		...signerThreeData,
		...signerFourData,
	};
};

export const mappedUpdateSignerData = formValues => {
	return {
		[`${ExecutiveSignerNameFields.signerOne}_${ExecutiveSignerNameFields.comment}`]:
			formValues?.[
				`${ExecutiveSignerNameFields.signerOne}_${ExecutiveSignerNameFields.comment}`
			],
		[`${ExecutiveSignerNameFields.signerTwo}_${ExecutiveSignerNameFields.comment}`]:
			formValues?.[
				`${ExecutiveSignerNameFields.signerTwo}_${ExecutiveSignerNameFields.comment}`
			],
		[`${ExecutiveSignerNameFields.signerThree}_${ExecutiveSignerNameFields.comment}`]:
			formValues?.[
				`${ExecutiveSignerNameFields.signerThree}_${ExecutiveSignerNameFields.comment}`
			],
		[`${ExecutiveSignerNameFields.signerFour}_${ExecutiveSignerNameFields.comment}`]:
			formValues?.[
				`${ExecutiveSignerNameFields.signerFour}_${ExecutiveSignerNameFields.comment}`
			],
	};
};

export const getMappedApprovedParametersData = data => {
	return {
		[ExecutiveApprovedParametersNameFields.monthlyVolume]:
			data?.[
				ExecutiveApprovedParametersNameFields.monthlyVolume
			]?.toString(),
		[ExecutiveApprovedParametersNameFields.maxTicket]:
			data?.[ExecutiveApprovedParametersNameFields.maxTicket]?.toString(),
		[ExecutiveApprovedParametersNameFields.avgTicket]:
			data?.[ExecutiveApprovedParametersNameFields.avgTicket]?.toString(),
	};
};

export const getMappedEstimatedExposureData = data => {
	return {
		[ExecutiveEstimatedExposureNameFields.deliverySameDay]:
			data?.[
				ExecutiveEstimatedExposureNameFields.deliverySameDay
			]?.toString(),
		[ExecutiveEstimatedExposureNameFields.deliveryFromOneToThreeDays]:
			data?.[
				ExecutiveEstimatedExposureNameFields.deliveryFromOneToThreeDays
			]?.toString(),
		[ExecutiveEstimatedExposureNameFields.deliveryFromFourToSevenDays]:
			data?.[
				ExecutiveEstimatedExposureNameFields.deliveryFromFourToSevenDays
			]?.toString(),
		[ExecutiveEstimatedExposureNameFields.deliveryFromEightToFourteenDays]:
			data?.[
				ExecutiveEstimatedExposureNameFields
					.deliveryFromEightToFourteenDays
			]?.toString(),
		[ExecutiveEstimatedExposureNameFields.deliveryFromFifteenToThirtyDays]:
			data?.[
				ExecutiveEstimatedExposureNameFields
					.deliveryFromFifteenToThirtyDays
			]?.toString(),
		[ExecutiveEstimatedExposureNameFields.deliveryOverThirtyDays]:
			data?.[
				ExecutiveEstimatedExposureNameFields.deliveryOverThirtyDays
			]?.toString(),
		[ExecutiveEstimatedExposureNameFields.futureDeliveryExposure]:
			data?.[
				ExecutiveEstimatedExposureNameFields.futureDeliveryExposure
			]?.toString(),
		[ExecutiveEstimatedExposureNameFields.totalCreditExposure]:
			data?.[
				ExecutiveEstimatedExposureNameFields.totalCreditExposure
			]?.toString(),
	};
};

export const getMappedRecommendationData = data => {
	return {
		[ExecutiveRecommendationNameFields.recommendation]: data,
	};
};

export const mappedRecommendationData = formValues => {
	return {
		[ExecutiveRecommendationNameFields.recommendation]:
			formValues?.[ExecutiveRecommendationNameFields.recommendation],
	};
};

export const getExecutiveAgentItems = data => {
	const underwritingApprovedAt = getFormattedDate({
		date: data?.underwritingApprovedAt,
		format: "MM/DD/YYYY",
	});

	const feeItem = getFeesItems(data?.fees)?.find(object => {
		const label = object.row[0].label;
		return (
			label === "Interchange+ V/MC/D, %" ||
			label === "Flat Rate, %" ||
			label === "Qual V/MC/D, %"
		);
	});

	const feItemValue = feeItem?.row?.[0]?.value;

	const pricingValue =
		feItemValue && `${float2Normalizer(feeItem?.row?.[0]?.value)} BPs/5c`;

	const renderAuroraProfitValue = () => {
		const value = data?.auroraProfit;
		if (typeof value !== "number") return;

		const previousMonth = moment().subtract(1, "month").format("MMMM");
		const isValueNegative = value < 0;
		const positiveValue = Math.abs(value);
		const normalizedValue = float2Normalizer(positiveValue);

		const result = `${
			isValueNegative ? "-" : ""
		} $${normalizedValue} (${previousMonth})`;

		return result;
	};

	return [
		{
			row: [
				{
					label: "Agent",
					value: getFullName(data?.agentName),
				},
				{
					label: "Pricing",
					value: pricingValue,
				},
			],
		},
		{
			row: [
				{
					label: "Aurora Profit",
					value: renderAuroraProfitValue(),
				},
				{
					label: "Bank",
					value: data?.bank?.name,
				},
			],
		},
		{
			row: [
				{
					label: "Approval Date",
					value: underwritingApprovedAt,
				},
			],
		},
	];
};

export const getApprovedParametersItems = data => {
	return [
		{
			row: [
				{
					label: "Monthly Volume",
					value: floatWithComma2Normalizer(
						data?.[
							ExecutiveApprovedParametersNameFields.monthlyVolume
						],
						"$",
					),
					labelCol: 2,
					valueCol: 10,
				},
			],
		},
		{
			row: [
				{
					label: "Max Ticket",
					value: floatWithComma2Normalizer(
						data?.[ExecutiveApprovedParametersNameFields.maxTicket],
						"$",
					),
					labelCol: 2,
					valueCol: 10,
				},
			],
		},
		{
			row: [
				{
					label: "Avg Ticket",
					value: floatWithComma2Normalizer(
						data?.[ExecutiveApprovedParametersNameFields.avgTicket],
						"$",
					),
					labelCol: 2,
					valueCol: 10,
				},
			],
		},
	];
};

export const getDeliveryDaysValue = (
	deliverySameDay,
	deliveryFromOneToThreeDays,
	deliveryFromFourToSevenDays,
	deliveryFromEightToFourteenDays,
	deliveryFromFifteenToThirtyDays,
	deliveryOverThirtyDays,
) => {
	return `${DaysToDeliveryFieldsNames.SameDay.displayName} ${deliverySameDay}% 
			/ ${DaysToDeliveryFieldsNames.OneThreeDays.displayName} ${deliveryFromOneToThreeDays}%
			/ ${DaysToDeliveryFieldsNames.FourSevenDays.displayName} ${deliveryFromFourToSevenDays}% 
			/ ${DaysToDeliveryFieldsNames.EightFourteenDays.displayName}  ${deliveryFromEightToFourteenDays}% 
			/ ${DaysToDeliveryFieldsNames.FifteenThirtyDays.displayName}  ${deliveryFromFifteenToThirtyDays}% 
			/ ${DaysToDeliveryFieldsNames.ThirtyPlusDays.displayName}   ${deliveryOverThirtyDays}%`;
};

export const getEstimatedExposureItems = data => {
	const deliverySameDay =
		data?.[ExecutiveEstimatedExposureNameFields.deliverySameDay];
	const deliveryFromOneToThreeDays =
		data?.[ExecutiveEstimatedExposureNameFields.deliveryFromOneToThreeDays];
	const deliveryFromFourToSevenDays =
		data?.[
			ExecutiveEstimatedExposureNameFields.deliveryFromFourToSevenDays
		];
	const deliveryFromEightToFourteenDays =
		data?.[
			ExecutiveEstimatedExposureNameFields.deliveryFromEightToFourteenDays
		];
	const deliveryFromFifteenToThirtyDays =
		data?.[
			ExecutiveEstimatedExposureNameFields.deliveryFromFifteenToThirtyDays
		];
	const deliveryOverThirtyDays =
		data?.[ExecutiveEstimatedExposureNameFields.deliveryOverThirtyDays];
	return [
		{
			row: [
				{
					label: "Delivery Days",
					value: getDeliveryDaysValue(
						deliverySameDay,
						deliveryFromOneToThreeDays,
						deliveryFromFourToSevenDays,
						deliveryFromEightToFourteenDays,
						deliveryFromFifteenToThirtyDays,
						deliveryOverThirtyDays,
					),
					labelCol: 2,
					valueCol: 10,
				},
			],
		},
		{
			row: [
				{
					label: "Future Delivery Exposure",
					value: floatWithComma2Normalizer(
						data?.[
							ExecutiveEstimatedExposureNameFields
								.futureDeliveryExposure
						] || "",
						"$",
					),
					labelCol: 2,
					valueCol: 10,
				},
			],
		},
		{
			row: [
				{
					label: "Total Credit Exposure",
					value: floatWithComma2Normalizer(
						data?.[
							ExecutiveEstimatedExposureNameFields
								.totalCreditExposure
						] || "",
						"$",
					),
					labelCol: 2,
					valueCol: 10,
				},
			],
		},
	];
};

export const mapFinancialsData = (data: { [key: string]: any }) => {
	return {
		[ExecutiveFinancialsFormFields.balanceDate]: getOnlyDate(
			data?.[ExecutiveFinancialsFormFields.balanceDate],
		),
		[ExecutiveFinancialsFormFields.balanceCash]:
			data?.[ExecutiveFinancialsFormFields.balanceCash],
		[ExecutiveFinancialsFormFields.balanceOtherCurrentAssets]:
			data?.[ExecutiveFinancialsFormFields.balanceOtherCurrentAssets],
		[ExecutiveFinancialsFormFields.balanceTotalCurrentAssets]:
			data?.[ExecutiveFinancialsFormFields.balanceTotalCurrentAssets],
		[ExecutiveFinancialsFormFields.balanceTotalAssets]:
			data?.[ExecutiveFinancialsFormFields.balanceTotalAssets],
		[ExecutiveFinancialsFormFields.balanceAccountsPayable]:
			data?.[ExecutiveFinancialsFormFields.balanceAccountsPayable],
		[ExecutiveFinancialsFormFields.balanceTotalCurrentLiabilities]:
			data?.[
				ExecutiveFinancialsFormFields.balanceTotalCurrentLiabilities
			],
		[ExecutiveFinancialsFormFields.balanceTotalLiabilities]:
			data?.[ExecutiveFinancialsFormFields.balanceTotalLiabilities],
		[ExecutiveFinancialsFormFields.balanceShareholderEquity]:
			data?.[ExecutiveFinancialsFormFields.balanceShareholderEquity],
		[ExecutiveFinancialsFormFields.balanceCurrentRatio]:
			data?.[ExecutiveFinancialsFormFields.balanceCurrentRatio],
		[ExecutiveFinancialsFormFields.balanceDebtRatio]:
			data?.[ExecutiveFinancialsFormFields.balanceDebtRatio],
		[ExecutiveFinancialsFormFields.balanceWorkingCapital]:
			data?.[ExecutiveFinancialsFormFields.balanceWorkingCapital],
		[ExecutiveFinancialsFormFields.balanceDebtToEquity]:
			data?.[ExecutiveFinancialsFormFields.balanceDebtToEquity],
		[ExecutiveFinancialsFormFields.plDate]: getOnlyDate(
			data?.[ExecutiveFinancialsFormFields.plDate],
		),
		[ExecutiveFinancialsFormFields.plTotalIncome]:
			data?.[ExecutiveFinancialsFormFields.plTotalIncome],
		[ExecutiveFinancialsFormFields.plCOGS]:
			data?.[ExecutiveFinancialsFormFields.plCOGS],
		[ExecutiveFinancialsFormFields.plGrossProfit]:
			data?.[ExecutiveFinancialsFormFields.plGrossProfit],
		[ExecutiveFinancialsFormFields.plOperatingExpenses]:
			data?.[ExecutiveFinancialsFormFields.plOperatingExpenses],
		[ExecutiveFinancialsFormFields.plOtherIncome]:
			data?.[ExecutiveFinancialsFormFields.plOtherIncome],
		[ExecutiveFinancialsFormFields.plTotalExpenses]:
			data?.[ExecutiveFinancialsFormFields.plTotalExpenses],
		[ExecutiveFinancialsFormFields.plNetIncome]:
			data?.[ExecutiveFinancialsFormFields.plNetIncome],
		[ExecutiveFinancialsFormFields.deferredRevenue]:
			data?.[ExecutiveFinancialsFormFields.deferredRevenue],
	};
};

const {
	AverageTicket,
	ChargebackTransactionsPercent,
	ChargebacksAmount,
	ChargebacksCount,
	ChargebacksVolume,
	CreditTransactionsPercent,
	CreditsAmount,
	CreditsAmountVolume,
	CreditsCount,
	LargestSaleAmount,
	ProcessingMonth,
	SalesAmount,
	SalesCount,
} = ExecutiveProcessingReviewFields;

// Table with 17 columns
export const getProcessingReviewItems = data => {
	const result = data?.map?.(item => ({
		row: [
			// Date
			{
				value: getFormattedDate({
					date: item?.[ProcessingMonth.name],
					format: "MM/DD/YYYY",
				}),
			},
			// empty column
			{
				value: "",
			},
			// Sales
			{
				value: item?.[SalesCount.name],
			},
			{
				value:
					item?.[SalesAmount.name] === 0
						? "-"
						: floatWithComma2Normalizer(item?.[SalesAmount.name]),
			},

			{
				value: floatWithComma2Normalizer(
					item?.[LargestSaleAmount.name],
				),
			},
			{
				value: floatWithComma2Normalizer(item?.[AverageTicket.name]),
			},
			// empty column
			{
				value: "",
			},
			// Credits
			{
				value: item?.[CreditsCount.name],
			},

			{
				value:
					item?.[CreditsAmount.name] === 0
						? "-"
						: floatWithComma2Normalizer(item?.[CreditsAmount.name]),
			},
			{
				value: float2Normalizer(item?.[CreditTransactionsPercent.name]),
			},
			{
				value: float2Normalizer(item?.[CreditsAmountVolume.name]),
			},
			// empty column
			{
				value: "",
			},
			// Chargebacks
			{
				value: item?.[ChargebacksCount.name],
			},
			{
				value:
					item?.[ChargebacksAmount.name] === 0
						? "-"
						: floatWithComma2Normalizer(
								item?.[ChargebacksAmount.name],
						  ),
			},
			{
				value: float2Normalizer(
					item?.[ChargebackTransactionsPercent.name],
				),
			},
			{
				value: float2Normalizer(item?.[ChargebacksVolume.name]),
			},
			// empty column
			{
				value: "",
			},
		],
	}));

	return result;
};

// Table with 17 columns
export const getProcessingReviewHeaderItems = () => {
	return [
		{
			row: [
				// Date
				{
					label: ProcessingMonth.displayName,
				},
				// empty column
				{
					label: "",
				},
				// Sales
				{
					label: SalesCount.displayName,
				},
				{
					label: SalesAmount.displayName,
				},
				{
					label: LargestSaleAmount.displayName,
				},
				{
					label: AverageTicket.displayName,
				},
				// empty column
				{
					label: "",
				},
				// Credits
				{
					label: CreditsCount.displayName,
				},

				{
					label: CreditsAmount.displayName,
				},
				{
					label: CreditTransactionsPercent.displayName,
				},
				{
					label: CreditsAmountVolume.displayName,
				},
				// empty column
				{
					label: "",
				},
				// Chargebacks
				{
					label: ChargebacksCount.displayName,
				},
				{
					label: ChargebacksAmount.displayName,
				},
				{
					label: ChargebackTransactionsPercent.displayName,
				},
				{
					label: ChargebacksVolume.displayName,
				},
				// empty column
				{
					label: "",
				},
			],
		},
	];
};

export const mapProcessingReviewDataFromServer = data => {
	const result = data.map(item => ({
		[AverageTicket.name]: item[AverageTicket.beName],
		[ChargebackTransactionsPercent.name]:
			item[ChargebackTransactionsPercent.beName],
		[ChargebacksAmount.name]: item[ChargebacksAmount.beName],
		[ChargebacksCount.name]: item[ChargebacksCount.beName],
		[ChargebacksVolume.name]: item[ChargebacksVolume.beName],
		[CreditTransactionsPercent.name]:
			item[CreditTransactionsPercent.beName],
		[CreditsAmount.name]: item[CreditsAmount.beName],
		[CreditsAmountVolume.name]: item[CreditsAmountVolume.beName],
		[CreditsCount.name]: item[CreditsCount.beName],
		[LargestSaleAmount.name]: item[LargestSaleAmount.beName],
		[ProcessingMonth.name]: item[ProcessingMonth.beName],
		[SalesAmount.name]: item[SalesAmount.beName],
		[SalesCount.name]: item[SalesCount.beName],
	}));

	return result;
};
